import type { AppNotification } from '@digital-twins/shared-types'

export interface NotificationsState {
  items: Array<AppNotification>
  cursor: string | null
  hasMore: boolean
  lastReadAt: number
  prevLastReadAt: number
  isLoading: boolean
  error: string | null
}

export type NotificationsAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_ERROR'; error: string }
  | { type: 'LOADED_PAGE'; items: Array<AppNotification>; cursor: string | null; hasMore: boolean; lastReadAt: number }
  | { type: 'PREPEND_ITEM'; item: AppNotification }
  | { type: 'MARK_ALL_READ'; at: number }
  | { type: 'MARK_ALL_READ_ROLLBACK' }
  | { type: 'LOAD_MORE'; items: Array<AppNotification>; cursor: string | null; hasMore: boolean }

export const initialNotificationsState: NotificationsState = {
  items: [],
  cursor: null,
  hasMore: false,
  lastReadAt: 0,
  prevLastReadAt: 0,
  isLoading: false,
  error: null,
}

export const initialState = initialNotificationsState

export function notificationsReducer(
  state: NotificationsState,
  action: NotificationsAction,
): NotificationsState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, isLoading: true, error: null }

    case 'LOAD_ERROR':
      return { ...state, isLoading: false, error: action.error }

    case 'LOADED_PAGE': {
      const lastReadAt = Math.max(action.lastReadAt, state.lastReadAt)
      return {
        ...state,
        items: mergeNotifications([], action.items),
        cursor: action.cursor,
        hasMore: action.hasMore,
        lastReadAt,
        prevLastReadAt: lastReadAt,
        isLoading: false,
        error: null,
      }
    }

    case 'PREPEND_ITEM': {
      const exists = state.items.some((n) => n.id === action.item.id)
      if (exists) return state
      return { ...state, items: [action.item, ...state.items] }
    }

    case 'MARK_ALL_READ':
      return {
        ...state,
        prevLastReadAt: state.lastReadAt,
        lastReadAt: action.at,
      }

    case 'MARK_ALL_READ_ROLLBACK':
      return {
        ...state,
        lastReadAt: state.prevLastReadAt,
      }

    case 'LOAD_MORE':
      return {
        ...state,
        items: mergeNotifications(state.items, action.items),
        cursor: action.cursor,
        hasMore: action.hasMore,
        isLoading: false,
        error: null,
      }

    default:
      return state
  }
}

export function selectUnreadCount(state: NotificationsState): number {
  return state.items.filter((item) => item.createdAt > state.lastReadAt).length
}

function mergeNotifications(
  current: Array<AppNotification>,
  incoming: Array<AppNotification>,
) {
  const seen = new Set<string>()
  return [...current, ...incoming]
    .filter((item) => {
      if (seen.has(item.id)) {
        return false
      }
      seen.add(item.id)
      return true
    })
    .sort((a, b) => b.createdAt - a.createdAt)
}
